export const NEWS_VIEW_SESSION_KEY = 'shot_news_view_session';

const SESSION_ID = /^[A-Za-z0-9-]{8,64}$/;

export const newsViewSentKey = (articleId: string): string => `shot_news_view_sent:${articleId}`;

export interface NewsViewStorage {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
}

export interface NewsViewBody {
  articleId: string;
  sessionId: string;
}

export interface NewsViewDeps {
  storage: NewsViewStorage | null;
  memory: Map<string, string>;
  createId: () => string;
  post: (body: NewsViewBody) => Promise<unknown>;
}

// WHY: без sessionStorage (приватный режим, запрет cookies) сессия и отметки живут в памяти вкладки — иначе каждый
// повторный монтаж получал бы новую сессию и обходил подавление повторов на сервере
export const NEWS_VIEW_MEMORY = new Map<string, string>();

export type NewsViewOutcome = 'disabled' | 'already_sent' | 'sent' | 'failed';

const read = (storage: NewsViewStorage | null, memory: Map<string, string>, key: string): string | null => {
  try {
    const stored = storage ? storage.getItem(key) : null;
    if (stored !== null) return stored;
  } catch {
    return memory.get(key) ?? null;
  }
  return memory.get(key) ?? null;
};

const write = (storage: NewsViewStorage | null, memory: Map<string, string>, key: string, value: string): void => {
  memory.set(key, value);
  try {
    storage?.setItem(key, value);
  } catch {
    return;
  }
};

export function newsViewSessionId(
  storage: NewsViewStorage | null,
  memory: Map<string, string>,
  createId: () => string,
): string {
  const stored = read(storage, memory, NEWS_VIEW_SESSION_KEY);
  if (stored !== null && SESSION_ID.test(stored)) return stored;
  const id = createId();
  write(storage, memory, NEWS_VIEW_SESSION_KEY, id);
  return id;
}

// WHY: счётчик не должен ломать страницу ни при каком сбое — хранилище, генератор id и сеть обёрнуты, исход только код;
// отметка об отправке ставится в момент отправки, чтобы повторный монтаж в той же вкладке не прислал просмотр второй раз
export async function registerNewsView(articleId: string, enabled: boolean, deps: NewsViewDeps): Promise<NewsViewOutcome> {
  if (!enabled) return 'disabled';
  try {
    if (read(deps.storage, deps.memory, newsViewSentKey(articleId)) !== null) return 'already_sent';
    const sessionId = newsViewSessionId(deps.storage, deps.memory, deps.createId);
    write(deps.storage, deps.memory, newsViewSentKey(articleId), '1');
    await deps.post({ articleId, sessionId });
    return 'sent';
  } catch {
    return 'failed';
  }
}

export interface NewsViewCrypto {
  randomUUID?: () => string;
  getRandomValues: (array: Uint8Array) => Uint8Array;
}

// WHY: randomUUID есть только в защищённом контексте — на http-стенде берём те же 16 случайных байт напрямую
export function createNewsSessionId(source: NewsViewCrypto): string {
  if (source.randomUUID) return source.randomUUID();
  return Array.from(source.getRandomValues(new Uint8Array(16)), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

export interface IdleScheduler {
  requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
  cancelIdleCallback?: (handle: number) => void;
  setTimeout: (callback: () => void, ms: number) => number;
  clearTimeout: (handle: number) => void;
}

export const NEWS_VIEW_IDLE_TIMEOUT_MS = 3000;
export const NEWS_VIEW_FALLBACK_DELAY_MS = 1500;

// WHY: в Safari нет requestIdleCallback — там откладываем таймером; отмена при размонтировании не засчитывает уход до простоя
// и не даёт двойному монтажу StrictMode без хранилища отправить два запроса
export function scheduleNewsView(host: IdleScheduler, run: () => void): () => void {
  if (host.requestIdleCallback) {
    const handle = host.requestIdleCallback(run, { timeout: NEWS_VIEW_IDLE_TIMEOUT_MS });
    return () => host.cancelIdleCallback?.(handle);
  }
  const handle = host.setTimeout(run, NEWS_VIEW_FALLBACK_DELAY_MS);
  return () => host.clearTimeout(handle);
}
